import { createSlice } from "@reduxjs/toolkit";

import { reset } from "./shared";
import { toUserId } from "../utils/toUserId";
import { courseQuestions } from "./tmp_smalltools";

const initialState = {
  stages: null,
  stageData: [],
  showLatestStage: false,
  userId: null,
  transitionsComplete: ["THEORY"], // ADDED TO PREVENT TRANSITIONS AS A TEST RUN.
};

export const delegateSlice = createSlice({
  name: "delegate",
  initialState,
  reducers: {
    login(state, action) {
      state.stages = action.payload;
    },
    completeTransition(state, action) {
      state.transitionsComplete.push(action.payload);
    },
    setStageData(state, action) {
      const data = action.payload;
      const currentStage = state.stages[state.stageData.length];
      const nextStage = state.stages[state.stageData.length + 1];

      if (currentStage.Type === "VORTEX_REGISTRATION") {
        state.userId = toUserId(data);
      }

      // Skip QR display if theory is followed by feedback (combine them)
      const shouldSkipQR =
        currentStage.Type === "THEORY" && nextStage?.Type === "FEEDBACK";

      state.showLatestStage = !shouldSkipQR;
      state.stageData.push(data);
    },
    addTheory(state) {
      const reg = state.stages[0];
      const feedback = state.stages.find((s) => s.Type === "FEEDBACK");
      const theory = {
        Name: "Theory",
        Type: "THEORY",
        Details: {
          CourseQuestions: courseQuestions.map((q) => {
            return {
              ...q,
              Answers: q.Answers.map((a) => {
                return a.Answer;
              }),
            };
          }),
          TotalToPass: 8,
          Primary: true,
        },
      };

      state.stageData = state.stageData.filter((s) => !s.GivenAnswers);

      state.stages = [reg, theory, feedback];
    },
    toggleShowLatestStage(state) {
      state.showLatestStage = !state.showLatestStage;
    },
  },
  extraReducers: (builder) => builder.addCase(reset, () => initialState),
});

export const {
  login,
  setStageData,
  toggleShowLatestStage,
  completeTransition,
  addTheory,
} = delegateSlice.actions;

export default delegateSlice.reducer;

export const currentStage = (state) => {
  if (!state.delegate.stages?.length) return null;
  return state.delegate.stages[state.delegate.stageData.length];
};

export const prevQRs = (state) => {
  const stageData = state.delegate.stageData;
  const stages = state.delegate.stages;

  const qrs = stageData
    .map((d, i) => {
      const stage = stages[i];
      const nextStage = stages[i + 1];

      // If this is theory followed by feedback, and we have feedback data
      if (
        stage.Type === "THEORY" &&
        nextStage?.Type === "FEEDBACK" &&
        stageData[i + 1]
      ) {
        // Return combined QR data
        return {
          name: stage.Name, // Keep "Theory" as the name
          data: {
            theoryData: d,
            feedbackData: stageData[i + 1],
          },
        };
      }

      // If this is feedback that was already combined, skip it
      if (stage.Type === "FEEDBACK" && stages[i - 1]?.Type === "THEORY") {
        return null; // Will be filtered out
      }

      // Normal single-stage QR
      return {
        name: stage.Name,
        data: d,
      };
    })
    .filter(Boolean) // Remove null entries
    .reverse();

  return qrs;
};

export const latestStage = (state) => {
  return state.delegate.stageData[state.delegate.stageData.length - 1];
};

export const shouldShowTransition = (state) => {
  if (
    state.user.type !== "DELEGATE" ||
    !state.delegate.stageData ||
    !state.delegate.stages
  )
    return false;

  if (
    state.delegate.stageData.length === state.delegate.stages.length &&
    !state.delegate.transitionsComplete.includes("FINAL")
  ) {
    return "FINAL";
  }

  const nextStage = currentStage(state);
  if (!nextStage?.Type) return false;

  const STAGES_WITH_TRANSITIONS_BEFORE = ["THEORY", "FEEDBACK"];
  return STAGES_WITH_TRANSITIONS_BEFORE.includes(nextStage.Type) &&
    !state.delegate.transitionsComplete.includes(nextStage.Type)
    ? nextStage.Type
    : false;
};

export const delegateName = (state) => {
  const data = state.delegate.stageData || [];
  const [rego] = data;
  if (!rego) return null;

  return rego.FirstName && rego.Surname
    ? `${rego.FirstName} ${rego.Surname}`
    : null;
};
